import { CHAT_CHANNEL_LATEST_MESSAGE, CHAT_CHANNEL_MEDIAS, CHAT_CHANNEL_MEMBERS, CHAT_CHANNEL_TYPING, CHAT_CHANNELS, ChatChannel, ChatChannelLatestMessage, ChatChannelMedia, ChatChannelMember } from "../../db/chat";
import { RealtimeDatabase, RealtimeReference } from "../../db/common";
import { BaseDatabaseService } from "../DatabaseService";
import { ChatDirectIndexService } from "./ChatDirectIndexService";

/**
 * Shared Chat Channel Service
 * Works with both Firebase Admin SDK (backend) and Client SDK (frontend)
 */
export class ChatChannelService extends BaseDatabaseService<ChatChannel> {
    protected getBasePath(): string {
        return CHAT_CHANNELS;
    }

    //make it public for sub on change for FE
    public getEntityRef(channelId: string): RealtimeReference {
        return super.getEntityRef(channelId);
    }

    //@overwrite
    async create(data: Omit<ChatChannel, typeof CHAT_CHANNEL_TYPING | typeof CHAT_CHANNEL_MEDIAS>, id?: string): Promise<string> {
        if (data.type === 'direct') {
            //if the channel is a 1-1 chat, check if direct index doesn't already exist
            const chatDirectIndexService = new ChatDirectIndexService(this.db, this.logger);
            const [userId1, userId2] = Object.keys(data[CHAT_CHANNEL_MEMBERS]);
            const channelId = await chatDirectIndexService
                .get(chatDirectIndexService.getEntityId(userId1, userId2))
            if (channelId !== null) {
                //if the direct chat already exist, simply return the existing channel id without creating a new one
                return channelId;
            }
        }
        return super.create({
            ...data as ChatChannel,
            [CHAT_CHANNEL_TYPING]: {},
            [CHAT_CHANNEL_MEDIAS]: {}
        }, id)
    }

    public membersService(channelId: string) {
        return new ChatChannelMembersService(this.db, channelId);
    }

    public lastMessageService(channelId: string) {
        return new ChatChannelLastMessageService(this.db, channelId);
    }

    public mediasService(channelId: string) {
        return new ChatChannelMediasService(this.db, channelId);
    }
}

class ChatChannelMembersService extends BaseDatabaseService<ChatChannelMember> {
    constructor(protected db: RealtimeDatabase, protected channelId: string) {
        super(db)
    }
    protected getBasePath(): string {
        return `${CHAT_CHANNELS}/${this.channelId}/${CHAT_CHANNEL_MEMBERS}`
    }

    //make it public for listening on child changes
    public getCollectionRef(): RealtimeReference {
        return super.getCollectionRef();
    }
}

class ChatChannelLastMessageService extends BaseDatabaseService<ChatChannelLatestMessage> {
    constructor(protected db: RealtimeDatabase, protected channelId: string) {
        super(db)
    }
    protected getBasePath(): string {
        return `${CHAT_CHANNELS}/${this.channelId}`
    }

    //make it public to be able to do transaction on it
    public getEntityRef(): RealtimeReference {
        return super.getEntityRef(CHAT_CHANNEL_LATEST_MESSAGE);
    }

    get(): Promise<ChatChannelLatestMessage | null> {
        return super.get(CHAT_CHANNEL_LATEST_MESSAGE)
    }

    create(data: ChatChannelLatestMessage): Promise<string> {
        return super.create(data, CHAT_CHANNEL_LATEST_MESSAGE)
    }

    update(_: string, data: Partial<Partial<ChatChannelLatestMessage>>): Promise<void> {
        return super.update(CHAT_CHANNEL_LATEST_MESSAGE, data)
    }

    delete(): Promise<void> {
        return super.delete(CHAT_CHANNEL_LATEST_MESSAGE)
    }

    exists(): Promise<boolean> {
        return super.exists(CHAT_CHANNEL_LATEST_MESSAGE);
    }

    batchCreate(_: ChatChannelLatestMessage[]): Promise<string[]> {
        return Promise.reject('Should not use this function')
    }

    batchDelete(_: string[]): Promise<void> {
        return Promise.reject('Should not use this function')
    }

    list(): Promise<Record<string, ChatChannelLatestMessage>> {
        return Promise.reject('Should not use this function')
    }
}

class ChatChannelMediasService extends BaseDatabaseService<ChatChannelMedia> {
    constructor(protected db: RealtimeDatabase, protected channelId: string) {
        super(db)
    }
    protected getBasePath(): string {
        return `${CHAT_CHANNELS}/${this.channelId}/${CHAT_CHANNEL_MEDIAS}`
    }
}